import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Package, Truck, CheckCircle, Clock, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface ReturnRequest {
  id: string;
  box_id: string;
  user_id: string;
  status: string;
  notes: string | null;
  requested_at: string;
  created_at: string;
  box: {
    id: string;
    name: string;
    box_number: string;
    location_status: string;
  } | null;
  profile: {
    full_name: string | null;
    address_line1: string | null;
    address_line2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
  } | null;
  user_email?: string;
}

const AdminReturnRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<ReturnRequest | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    fetchReturnRequests();
  }, []);

  const fetchReturnRequests = async () => {
    try {
      setLoading(true);
      
      // First fetch return requests
      const { data: returnData, error: returnError } = await supabase
        .from('return_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (returnError) throw returnError;

      // Fetch associated boxes and profiles
      const enrichedRequests = await Promise.all(
        (returnData || []).map(async (request) => {
          // Fetch box info
          const { data: boxData } = await supabase
            .from('boxes')
            .select('id, name, box_number, location_status')
            .eq('id', request.box_id)
            .single();

          // Fetch profile info
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name, address_line1, address_line2, city, state, zip, phone')
            .eq('id', request.user_id)
            .single();

          // Fetch user email via edge function
          let userEmail = '';
          try {
            const { data: userData } = await supabase.functions.invoke('admin-list-users', {
              body: { search: request.user_id, limit: 1 }
            });
            if (userData?.users?.[0]?.email) {
              userEmail = userData.users[0].email;
            }
          } catch (e) {
            console.error('Failed to fetch user email:', e);
          }

          return {
            ...request,
            box: boxData,
            profile: profileData,
            user_email: userEmail
          };
        })
      );

      setRequests(enrichedRequests);
    } catch (error: any) {
      console.error('Error fetching return requests:', error);
      toast.error('Failed to load return requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'label_generated':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200"><Package className="w-3 h-3 mr-1" />Label Ready</Badge>;
      case 'shipped':
        return <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200"><Truck className="w-3 h-3 mr-1" />Shipped</Badge>;
      case 'delivered':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Delivered</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleGenerateLabel = async () => {
    if (!selectedRequest) return;

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-return-label', {
        body: {
          return_request_id: selectedRequest.id,
          box_id: selectedRequest.box_id,
          user_id: selectedRequest.user_id,
          admin_notes: adminNotes
        }
      });

      if (error) throw error;

      toast.success('Return shipping label generated and customer notified!');
      setShowLabelDialog(false);
      setSelectedRequest(null);
      setAdminNotes("");
      fetchReturnRequests();
    } catch (error: any) {
      console.error('Error generating label:', error);
      toast.error(error.message || 'Failed to generate return label');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateStatus = async (requestId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('return_requests')
        .update({ status: newStatus })
        .eq('id', requestId);

      if (error) throw error;

      toast.success(`Status updated to ${newStatus}`);
      fetchReturnRequests();
    } catch (error: any) {
      toast.error('Failed to update status');
    }
  };

  const handleMarkDelivered = async (request: ReturnRequest) => {
    try {
      // Update return request status
      await supabase
        .from('return_requests')
        .update({ status: 'delivered' })
        .eq('id', request.id);

      // Update box location status to 'home'
      await supabase
        .from('boxes')
        .update({ 
          location_status: 'home',
          return_requested_at: null
        })
        .eq('id', request.box_id);

      toast.success('Box marked as delivered and returned home');
      fetchReturnRequests();
    } catch (error: any) {
      toast.error('Failed to mark as delivered');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
          <h1 className="text-4xl font-bold mb-2">Return Requests</h1>
          <p className="text-muted-foreground">Manage customer box return requests</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Pending</CardDescription>
              <CardTitle className="text-2xl text-yellow-600">
                {requests.filter(r => r.status === 'pending').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Approved</CardDescription>
              <CardTitle className="text-2xl text-blue-600">
                {requests.filter(r => r.status === 'approved').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Label Ready</CardDescription>
              <CardTitle className="text-2xl text-purple-600">
                {requests.filter(r => r.status === 'label_generated').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Shipped</CardDescription>
              <CardTitle className="text-2xl text-indigo-600">
                {requests.filter(r => r.status === 'shipped').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Delivered</CardDescription>
              <CardTitle className="text-2xl text-green-600">
                {requests.filter(r => r.status === 'delivered' || r.status === 'completed').length}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Requests Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Return Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No return requests yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requested</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Box</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(request.requested_at), 'MMM d, yyyy')}
                        <br />
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(request.requested_at), 'h:mm a')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{request.profile?.full_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{request.user_email}</div>
                        <div className="text-xs text-muted-foreground">{request.profile?.phone}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{request.box?.name || 'Unknown Box'}</div>
                        <div className="text-xs text-muted-foreground">#{request.box?.box_number}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {request.profile?.address_line1 && (
                          <>
                            {request.profile.address_line1}
                            {request.profile.address_line2 && <>, {request.profile.address_line2}</>}
                            <br />
                            {request.profile.city}, {request.profile.state} {request.profile.zip}
                          </>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="max-w-[150px] truncate" title={request.notes || ''}>
                        {request.notes || '-'}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        {request.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleUpdateStatus(request.id, 'approved')}
                            >
                              Approve
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowLabelDialog(true);
                              }}
                            >
                              <Truck className="h-3 w-3 mr-1" />
                              Generate Label
                            </Button>
                          </>
                        )}
                        {request.status === 'approved' && (
                          <Button 
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowLabelDialog(true);
                            }}
                          >
                            <Truck className="h-3 w-3 mr-1" />
                            Generate Label
                          </Button>
                        )}
                        {request.status === 'label_generated' && (
                          <Button 
                            size="sm"
                            onClick={() => handleUpdateStatus(request.id, 'shipped')}
                          >
                            Mark Shipped
                          </Button>
                        )}
                        {request.status === 'shipped' && (
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkDelivered(request)}
                          >
                            Mark Delivered
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Generate Label Dialog */}
        <Dialog open={showLabelDialog} onOpenChange={setShowLabelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Return Shipping Label</DialogTitle>
              <DialogDescription>
                This will create a UPS shipping label from the facility to the customer and send them a notification email.
              </DialogDescription>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <p><strong>Box:</strong> {selectedRequest.box?.name} (#{selectedRequest.box?.box_number})</p>
                  <p><strong>Customer:</strong> {selectedRequest.profile?.full_name}</p>
                  <p><strong>Ship To:</strong></p>
                  <p className="text-sm">
                    {selectedRequest.profile?.address_line1}<br />
                    {selectedRequest.profile?.address_line2 && <>{selectedRequest.profile.address_line2}<br /></>}
                    {selectedRequest.profile?.city}, {selectedRequest.profile?.state} {selectedRequest.profile?.zip}
                  </p>
                </div>

                {selectedRequest.notes && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    <p className="text-sm font-medium text-yellow-800">Customer Notes:</p>
                    <p className="text-sm text-yellow-700">{selectedRequest.notes}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">Admin Notes (optional)</label>
                  <Textarea 
                    placeholder="Any notes about this return..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLabelDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerateLabel} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Truck className="h-4 w-4 mr-2" />
                    Generate & Notify
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminReturnRequests;
